/**
 * Wire schemas for the Proximi.io REST surface (zod-typed).
 *
 * These mirror the real public API endpoints the Phase-2 Rust BFF
 * (`proximiio-mcpd`) will proxy/back:
 *   - POST /core_auth/login        → auth
 *   - GET  /core/places            → venues' places (POIs)
 *   - GET  /core/floors?place_id=  → a place's floors/levels
 *   - GET  /v7/geo/features        → paginated GeoJSON features
 *   - GET  /v5/geo/style           → MapLibre style document
 *
 * Schemas are intentionally permissive (`.passthrough()` / `.partial()` where
 * the upstream payload is large or evolving) so a field addition upstream does
 * not throw at runtime; the fields we actually read are validated.
 */
import { z } from "zod";

/* ── Auth ──────────────────────────────────────────────────────────────── */

export const loginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const organizationSchema = z
  .object({
    id: z.string(),
    name: z.string().optional(),
  })
  .passthrough();
export type Organization = z.infer<typeof organizationSchema>;

export const userSchema = z
  .object({
    id: z.string(),
    email: z.string().optional(),
    name: z.string().optional(),
  })
  .passthrough();
export type User = z.infer<typeof userSchema>;

export const loginResponseSchema = z.object({
  token: z.string(),
  organization: organizationSchema,
  user: userSchema,
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

/* ── Places ────────────────────────────────────────────────────────────── */

export const placeSchema = z
  .object({
    id: z.string(),
    name: z.string().optional(),
    location: z
      .object({ lat: z.number(), lng: z.number() })
      .partial()
      .optional(),
  })
  .passthrough();
export type Place = z.infer<typeof placeSchema>;

export const placesResponseSchema = z.array(placeSchema);
export type PlacesResponse = z.infer<typeof placesResponseSchema>;

/* ── Floors ────────────────────────────────────────────────────────────── */

export const floorSchema = z
  .object({
    id: z.string(),
    name: z.string().optional(),
    /** Ordinal level index used by the floor switcher. */
    level: z.number().optional(),
    place_id: z.string().optional(),
  })
  .passthrough();
export type Floor = z.infer<typeof floorSchema>;

export const floorsResponseSchema = z.array(floorSchema);
export type FloorsResponse = z.infer<typeof floorsResponseSchema>;

/* ── Amenities (POI icons) ─────────────────────────────────────────────── */

/**
 * A POI amenity. `icon` is the renderable image — either a `data:image/*`
 * URI (the common case in Proximi.io) or an absolute/relative image URL. The
 * `styleimagemissing` handler keys the map's `icon-image` ids on `id`.
 */
export const amenitySchema = z
  .object({
    id: z.string(),
    icon: z.string().nullable().optional(),
    title: z.string().optional(),
    category: z.string().optional(),
  })
  .passthrough();
export type Amenity = z.infer<typeof amenitySchema>;

export const amenitiesResponseSchema = z.array(amenitySchema);
export type AmenitiesResponse = z.infer<typeof amenitiesResponseSchema>;

/* ── Geo features (paginated GeoJSON) ──────────────────────────────────── */

/**
 * A GeoJSON feature, kept **intact** for MapLibre. The org style's layer filters
 * key on the feature's top-level string `id` (`"<org>:<uuid>"`, promoted for
 * feature-state) and on `properties.*` (`type`, `amenity`, `title`, `level`,
 * `icon_only`, `text_only`, `__level_*`, …), so nothing here strips those:
 *   - `id` — `string | number`, preserved (Proximi.io ids are strings);
 *   - `geometry` — `z.unknown()`, passed through verbatim;
 *   - `properties` — `z.record(z.unknown())`, which keeps **every** key/value
 *     (records don't strip unknown keys) so the raw props reach `setData` whole;
 *   - `.passthrough()` — any other top-level members survive too.
 */
export const geoFeatureSchema = z
  .object({
    type: z.literal("Feature"),
    id: z.union([z.string(), z.number()]).optional(),
    geometry: z.unknown(),
    properties: z.record(z.unknown()).nullable().optional(),
  })
  .passthrough();
export type GeoFeature = z.infer<typeof geoFeatureSchema>;

/** v7 geo features are paginated; the body carries the page + cursor metadata. */
export const geoFeaturesPageSchema = z
  .object({
    features: z.array(geoFeatureSchema),
    /** Cursor / total metadata (shape varies by API version). */
    total: z.number().optional(),
    page: z.number().optional(),
    has_next: z.boolean().optional(),
    next: z.string().nullable().optional(),
  })
  .passthrough();
export type GeoFeaturesPage = z.infer<typeof geoFeaturesPageSchema>;

/* ── Map style (MapLibre GL style document) ────────────────────────────── */

export const mapStyleSchema = z
  .object({
    version: z.number(),
    sources: z.record(z.unknown()),
    layers: z.array(z.unknown()),
  })
  .passthrough();
export type MapStyle = z.infer<typeof mapStyleSchema>;
